/**
 * ──────────────────────────────────────────────────────────────────────────────
 *  appOnHover directive that exposes the “hover” state of an element
 * ──────────────────────────────────────────────────────────────────────────────
 *
 * How it works
 * ------------
 * • Listens to **mouseenter** / **mouseleave** on the host element.
 * • Internally stores the state in a `BehaviorSubject<boolean>`.
 * • Exposes the state in two flavours:
 *    1. `hovered`  – a plain boolean getter (cheap to read, no subscriptions)
 *    2. `hover$`   – an `Observable<boolean>` you can pipe, debounce, etc.
 *
 * Usage
 * -------------
 * ```html
 * <!-- Example A – boolean getter -->
 * <tr appOnHover #your_row_variable_name="onHover">
 *   <app-all-icons *ngIf="your_row_variable_name.hovered" [iconName]="edit.svg"></app-all-icons>
 * </tr>
 *
 * <!-- Example B – Observable with the async‑pipe -->
 * <tr appOnHover #your_row_variable_name="onHover">
 *   <app-all-icons *ngIf="your_row_variable_name.hover$ | async" [iconName]="edit.svg"></app-all-icons>
 * </tr>
 * ```
 *
 * Choose the flavour that fits your scenario:
 *   • `hovered` is simplest when you just need a flag in the template.
 *   • `hover$` is handy if you want RxJS operators (debounce, combineLatest, …)
 *     or if the host component uses OnPush change‑detection and the hover event
 *     may occur outside Angular’s zone.
 */
import { Directive, HostListener } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

@Directive({
  selector: '[appOnHover]',
  standalone: true,
  exportAs: 'onHover',
})
export class OnHoverDirective {
  /** Internal subject keeping the current hover state */
  private readonly _hover$ = new BehaviorSubject<boolean>(false);

  /**
   * Public observable that emits **true** on mouse‑enter and **false** on
   * mouse‑leave.  `distinctUntilChanged()` guarantees no duplicate emissions.
   */
  readonly hover$: Observable<boolean> = this._hover$.pipe(distinctUntilChanged());

  /** Plain boolean getter for lightweight template checks */
  get hovered(): boolean {
    return this._hover$.value;
  }

  @HostListener('mouseenter')
  private onEnter(): void {
    this._hover$.next(true);
  }

  @HostListener('mouseleave')
  private onLeave(): void {
    this._hover$.next(false);
  }
}
